/**
 * AuthDialog — Phase E split (May 6, 2026).
 *
 * State owner that routes the active modal variant based on
 * `authModal` from the auth slice. Replaces the monolithic
 * `AuthModals.jsx` (which is kept as a thin re-export shim so existing
 * `import AuthModals from '...'` paths keep working).
 *
 * Behavior, side-effects, and data-testids are byte-identical.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import useStore from '@/store/useStore';
import { Button } from '@/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/ui/dialog';
import SignInForm from './SignInForm';
import SignUpForm from './SignUpForm';
import ForgotPasswordForm from './ForgotPasswordForm';

const AuthDialog = () => {
  const authModal = useStore((s) => s.authModal);
  const setAuthModal = useStore((s) => s.setAuthModal);
  const signIn = useStore((s) => s.signIn);
  const signUp = useStore((s) => s.signUp);
  const authLoading = useStore((s) => s.authLoading);
  const authError = useStore((s) => s.authError);
  const clearAuthError = useStore((s) => s.clearAuthError);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDisplayName('');
    setShowPassword(false);
    clearAuthError();
  };

  const handleClose = () => {
    resetForm();
    setAuthModal(null);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    const result = await signIn(email, password);
    if (result.success) {
      toast.success('Signed in successfully!');
      resetForm();
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!email || !password || !confirmPassword || !displayName) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    const result = await signUp(email, password, displayName);
    if (result.success) {
      toast.success('Account created successfully!');
      resetForm();
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    // Mock - would call real API in production
    toast.success('Password reset link sent to your email');
    setAuthModal('signin');
  };

  const isOpen = authModal !== null;

  // Sign In Gate Modal
  if (authModal === 'signin-gate') {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md" data-testid="auth-gate-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-white font-heading">
              Sign In to EOC Closet to be able to Load or Save Designs
            </DialogTitle>
          </DialogHeader>
          <DialogFooter className="border-slate-700 bg-slate-800">
            <Button
              onClick={handleClose}
              variant="ghost"
              className="text-slate-400"
            >
              Cancel
            </Button>
            <Button
              onClick={() => setAuthModal('signin')}
              className="bg-teal-500 hover:bg-teal-400 text-slate-900"
              data-testid="goto-signin-btn"
            >
              Sign In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (authModal === 'signin') {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md" data-testid="signin-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-white font-heading">
              Sign In to EOC Closet
            </DialogTitle>
          </DialogHeader>
          <SignInForm
            email={email}
            password={password}
            showPassword={showPassword}
            authError={authError}
            authLoading={authLoading}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onToggleShowPassword={() => setShowPassword(!showPassword)}
            onSubmit={handleSignIn}
            onForgotPassword={() => setAuthModal('forgot')}
            onCreateAccount={() => setAuthModal('signup')}
          />
        </DialogContent>
      </Dialog>
    );
  }

  if (authModal === 'forgot') {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md" data-testid="forgot-password-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-white font-heading">
              Enter Your Registered Email To Receive A Password Reset Link
            </DialogTitle>
          </DialogHeader>
          <ForgotPasswordForm
            email={email}
            authLoading={authLoading}
            onEmailChange={setEmail}
            onSubmit={handleForgotPassword}
            onSwitchToSignIn={() => setAuthModal('signin')}
          />
        </DialogContent>
      </Dialog>
    );
  }

  if (authModal === 'signup') {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md" data-testid="signup-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-white font-heading">
              Sign Up For EOC Closet
            </DialogTitle>
          </DialogHeader>
          <SignUpForm
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            displayName={displayName}
            showPassword={showPassword}
            authError={authError}
            authLoading={authLoading}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onConfirmPasswordChange={setConfirmPassword}
            onDisplayNameChange={setDisplayName}
            onSubmit={handleSignUp}
            onSwitchToSignIn={() => setAuthModal('signin')}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};

export default AuthDialog;
